import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { Volume } from '@/api/type-server'
import { Divider, Modal, NumberInput, Trans } from '@/components'
import { InsufficientQuotaView } from '@/components/InsufficientQuotaView'
import { MachineType, PaymentType } from '@/consts/machine'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import { cn } from '@/utils/style.ts'

import { BindNATModal } from './components/BindNATModal'
import { ConfirmationModal } from './components/ConfirmationModal' // Added this line
import { MachineSelector } from './components/machine-selector'
import { MountDriveModal } from './components/MountDriveModal'
import { MountDriveSection } from './components/MountDriveSection'
import { OrderSummary } from './components/summary'
import { useMountConfig } from './hooks/useMountConfig'
import { buildCreateInstancePayload, filterValidVolumes } from './utils'

const ImageQtyMap: Record<string, number> = {
  'img-5yp6v8l8': 4,
  'img-y4lvnxqw': 2,
}

export function CreateInstancePage() {
  const navigate = useNavigate()

  const [type, setType] = useState(MachineType.GPU)
  const period = PaymentType.Hourly
  const [machine, setMachine] = useState<Machine>()
  const [count, setCount] = useState(1)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isCreatingIns, setIsCreatingIns] = useState(false)
  const [checkedImage, setCheckedImage] = useState<Image>()
  const [checkedSnap, setCheckedSnap] = useState<Snapshot>()

  // Mount Drive
  const [mountModalOpen, setMountModalOpen] = useState(false)
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>([])
  const { mountVolume, mountPath, readWriteMode, updateMountConfig, resetMountConfig } =
    useMountConfig()

  const [isSummaryDetailOpen, setIsSummaryDetailOpen] = useState(false)
  const summaryRef = useRef(null)

  const [boundNAT, setBoundNAT] = useState<any>(null)
  const [bindNATModalOpen, setBindNATModalOpen] = useState(false)

  useEffect(() => {
    setCount(machine?.unitStep ?? 1)
    resetMountConfig()
  }, [machine])

  useEffect(() => {
    if (checkedImage) {
      const qty = ImageQtyMap[checkedImage.resID] ?? 1
      setCount(qty)
    }
  }, [checkedImage])
  const canCreate = useMemo(() => {
    return machine && (checkedImage || checkedSnap)
  }, [machine, checkedImage, checkedSnap])

  const onConfirm = useCallback(
    async (name: string) => {
      if (!machine) return
      setIsCreatingIns(true)

      const data = buildCreateInstancePayload({
        machine,
        count,
        checkedImage,
        checkedSnap,
        mountVolume,
        mountPath,
        readWriteMode,
        boundNAT,
        remark: name,
      })

      const res = await API.createInstance(data)
      if (res.code === 0) {
        setIsConfirmModalOpen(false)
        navigate('/instances')
      }
      setIsCreatingIns(false)
    },
    [
      machine,
      count,
      type,
      checkedImage,
      checkedSnap,
      mountVolume,
      mountPath,
      readWriteMode,
      boundNAT,
    ],
  )

  useEffect(() => {
    setMachine(undefined)
    resetMountConfig()
  }, [type])

  const loadVolumesData = () => {
    API.getVolumes().then((response: any) => {
      if (response?.code == 0) {
        const volumes = response?.volumes || []
        const filteredVolumes = filterValidVolumes(volumes, machine?.domains ?? [])
        setCloudVolumes(filteredVolumes)
      }
    })
  }

  useEffect(() => {
    loadVolumesData()
  }, [machine])

  return (
    <div className="h-full relative">
      <div className="card !px-0 bg-white h-full overflow-y-auto">
        <div className="flex h-[60px] items-start justify-between px-8">
          <div className="h6">
            <Trans i18nKey="create.createNew">Deploy</Trans>
          </div>
        </div>

        <Divider className="px-8" />

        <div className="px-8">
          <MachineSelector
            type={type}
            period={period}
            onTypeChange={setType}
            onChange={setMachine}
            count={count}
            checkedImage={checkedImage}
            setCheckedImage={setCheckedImage}
            checkedSnap={checkedSnap}
            setCheckedSnap={setCheckedSnap}
          />
        </div>

        <div className="mt-8 px-8">
          <div className="t-sb mb-2 text-secondary">
            <Trans i18nKey="create.leasedQuantity">Unit Qty</Trans>
          </div>

          {/* Unit Quantity */}
          <NumberInput
            value={count}
            onChange={(value) => setCount(value)}
            min={Math.max(
              machine?.unitStep ?? 1,
              checkedImage ? (ImageQtyMap[checkedImage.resID] ?? 1) : 1,
            )}
            max={machine?.maxAvailUnits ?? 1}
            step={machine?.unitStep ?? 1}
            className="w-[260px]"
            disabled={!machine}
          />
          {!machine && (
            <div className="t-sm text-warning mt-2">
              <Trans i18nKey="create.selectMachineWarning">* Please select the machine first</Trans>
            </div>
          )}
        </div>

        <MountDriveSection
          machine={machine}
          mountVolume={mountVolume}
          mountPath={mountPath}
          readWriteMode={readWriteMode}
          cloudVolumes={cloudVolumes}
          setMountModalOpen={setMountModalOpen}
          resetMountConfig={resetMountConfig}
          navigate={navigate}
        />

        <div
          ref={summaryRef}
          className={cn(
            'absolute bottom-[2px] left-[2px] right-[2px] -shadow-card-100 bg-white px-8 py-6 -mt-8 z-20 flex',
            {
              card: isSummaryDetailOpen,
              'rounded-b-[24px]': !isSummaryDetailOpen,
            },
          )}
        >
          <div className="w-full">
            <OrderSummary
              type={type}
              period={period}
              machine={machine}
              nat={boundNAT}
              count={count}
              disabled={!canCreate}
              onConfirm={() => setIsConfirmModalOpen(true)}
              onDetailToggle={(isOpen) => setIsSummaryDetailOpen(isOpen)}
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={isConfirmModalOpen}
        isCreatingIns={isCreatingIns}
        machine={machine}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={onConfirm}
      />

      {mountModalOpen &&
        (cloudVolumes.length > 0 ? (
          <MountDriveModal
            open={mountModalOpen}
            mountVolume={mountVolume}
            volumes={cloudVolumes}
            mountPath={mountPath}
            readWriteMode={readWriteMode}
            machineDomains={machine?.domains}
            onChange={(volume, path, mode) => {
              updateMountConfig(volume, path, mode)
              setMountModalOpen(false)
            }}
            onClose={() => setMountModalOpen(false)}
          />
        ) : (
          <Modal open={mountModalOpen} onClose={() => setMountModalOpen(false)}>
            <Modal.Header>Data Hub</Modal.Header>
            <Modal.Body>
              <InsufficientQuotaView />
            </Modal.Body>
          </Modal>
        ))}

      {bindNATModalOpen && (
        <BindNATModal
          open={bindNATModalOpen}
          domain={machine?.domains?.[0]}
          boundNAT={boundNAT}
          onClose={() => setBindNATModalOpen(false)}
          onOk={(selectedIp) => {
            if (selectedIp) setBoundNAT(selectedIp)
            setBindNATModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
